class DateTimeService {
  private regExpDateMask = /(YYYY|YY|MMMM|MMM|MM|M|DD|D|dd|d|HH|H|mm|ss|SSS)/g;
  private regExpDateMaskExtended = /{(YYYY|YY|MMMM|MMM|MM|M|DD|D|dd|d|HH|H|mm|ss|SSS)}/g;
  private monthNames: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  private monthNamesShort: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  private weekDayNames2Letters: string[] = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  public now(){
    const now = new Date();
    return now;
  }

  public today(){
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  public toEndOfDay(value: Date){
    const date = new Date(value.getTime());
    date.setDate(date.getDate() + 1);
    date.setTime(date.getTime() - 1);
    return date;
  }

  public addDays(value: Date, days: number){
    const date = new Date(value.getTime());
    date.setDate(date.getDate() + days);
    return date;
  }

  public addSeconds(value: Date, seconds: number){
    const date = new Date(value.getTime());
    date.setSeconds(date.getSeconds() + seconds);
    return date;
  }

  public addMinutes(value: Date, minutes: number){
    const date = new Date(value.getTime());
    date.setMinutes(date.getMinutes() + minutes);
    return date;
  }

  public roundBy(value: Date, roundMs: number){
    return new Date(Math.round(value.getTime() / roundMs) * roundMs);
  }

  public isSameDate(d1: Date | null, d2: Date | null): boolean {
    if (d1 === d2) return true;
    if (!d1 || !d2) return false;

    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }

  public diffDays(dateFrom: Date, dateTo: Date) {
    // Take the difference between the dates and divide by milliseconds per day.
    // Round to nearest whole number to deal with DST.
    return Math.round((dateTo.getTime() - dateFrom.getTime())/(1000*60*60*24));
  }

  public getZeroDate() {
    return new Date(0);
  }

  public getNanDate(): Date {
    return new Date(NaN);
  }

  private _parseSafeInteger(value: string) {
    const parsedPart = parseInt(value, 10);
    if (isNaN(parsedPart) || parsedPart < 0 || !Number.isInteger(parsedPart)) {
      throw new Error('Failed parse ' + value + ' exception, expected format [0-9]');
    }

    return parsedPart;
  }

  public toShortestUiDateTime(date: Date){
    const today = this.today();
    if (this.isSameDate(today, date)){
      return this.format(date, 'HH:mm:ss');
    }
    return this.format(date, 'DD MMM, HH:mm:ss');
  }

  public toJSON(value: Date){
    const year = value.getUTCFullYear().toString();
    let month = (value.getUTCMonth() + 1).toString();
    let day = value.getUTCDate().toString();
    let hh = value.getUTCHours().toString();
    let mm = value.getUTCMinutes().toString();
    let ss = value.getUTCSeconds().toString();

    if (month.length === 1)
      month = '0' + month;
    if (day.length === 1)
      day = '0' + day;
    if (hh.length === 1)
      hh = '0' + hh;
    if (mm.length === 1)
      mm = '0' + mm;
    if (ss.length === 1)
      ss = '0' + ss;

    //'YYYY-MM-DDTHH:MM:SS.Z'
    return `${year}-${month}-${day}T${hh}:${mm}:${ss}Z`;
  }
  
  public format(date: Date | null, format: string, defaultText?: string, isExtended?: boolean): string {
    if (!date) return defaultText || '-';
    let result = format;
    const regex = isExtended ? this.regExpDateMaskExtended : this.regExpDateMask;
    result = result.replace(regex, (match: string) => {
      switch (match.replace('{', '').replace('}', '')) {
        case 'DD':
          return date.getDate() < 10 ? '0' + date.getDate() : date.getDate().toString();
        case 'D':
          return date.getDate().toString();
        case 'dd':
          // eslint-disable-next-line no-case-declarations
          let dayOfWeek = date.getDay() - 1;
          if (dayOfWeek < 0) {
            dayOfWeek = 6;
          }
          return this.weekDayNames2Letters[dayOfWeek];
        case 'MMMM':
          return this.monthNames[date.getMonth()];
        case 'MMM':
          return this.monthNamesShort[date.getMonth()];
        case 'MM':
          return date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1).toString() : (date.getMonth() + 1).toString();
        case 'M':
          return (date.getMonth() + 1).toString();
        case 'YYYY':
          return date.getFullYear().toString();
        case 'YY':
          return (date.getFullYear() - 2000).toString();
        case 'HH':
          return date.getHours() < 10 ? '0' + date.getHours() : date.getHours().toString();
        case 'H':
          return date.getHours().toString();
        case 'mm':
          return date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes().toString();
        case 'ss':
          return date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds().toString();

        default:
          return match;
      }
    });
    return result;
  }
  
  private readonly patternCache: { [key: string]: { regex: RegExp, patterns: string[] } } = {};
  private readonly patternCacheExtended: { [key: string]: { regex: RegExp, patterns: string[] } } = {};
  private readonly patternsToRegex: { [key: string]: string } = {
    'YYYY': '(\\d{4})',
    'YY': '(\\d{2})',
    'MMMM': '(\\S+)',
    'MM': '(\\d{2})',
    'M': '(\\d{1,2})',
    'DD': '(\\d{2})',
    'D': '(\\d{1,2})',
    'HH': '(\\d{2})',
    'H': '(\\d{1,2})',
    'mm': '(\\d{2})',
    'ss': '(\\d{2})',
    'SSS': '(\\d{3})',
  };

  public parse(value: string, format: string, options?: { baseDate?: Date, isExtended?: boolean }): Date {
    if (typeof (value) !== 'string')
      return this.getNanDate();

    let year = 1900;
    let month = 0;
    let day = 1;
    let hour = 0;
    let minute = 0;
    let second = 0;
    let milliseconds = 0;
    
    const baseDate = options?.baseDate;
    if (baseDate){
      year = baseDate.getFullYear();
      month = baseDate.getMonth();
      day = baseDate.getDate();
      hour = baseDate.getHours();
      minute = baseDate.getMinutes();
      second = baseDate.getSeconds();
      milliseconds = baseDate.getMilliseconds();
    }
    
    const cache = options?.isExtended ? this.patternCacheExtended : this.patternCache;
    const patternCache = cache[format];

    let patterns: string[];
    let regexp: RegExp;
    if (patternCache){
      regexp = patternCache.regex;
      patterns = patternCache.patterns;
    } else {
      patterns = [];
      const regex = options?.isExtended ? this.regExpDateMaskExtended : this.regExpDateMask;
      const regexpString = format.replace(/[.+\\]/g, '\\$&').replace(regex, (_match, pattern) => {
        if (options?.isExtended){
          pattern = pattern.replace('{', '').replace('}', '');
        }
        patterns.push(pattern);
        return this.patternsToRegex[pattern];
      });

      regexp = new RegExp(regexpString);
      cache[format] = {
        regex: regexp,
        patterns: patterns
      };
    }

    const match = value.match(regexp);
    if (match) {
      for (let i = 1; i < match.length; i++) {
        const foundValue = patterns[i - 1];
        const valuePart = match[i];

        switch (foundValue) {
          case 'YYYY':
            year = this._parseSafeInteger(valuePart);
            break;
          case 'YY':
            year = this._parseSafeInteger(valuePart);
            if (year < 50) {
              year = year + 2000;
            } else {
              year = year + 1900;
            }
            break;
          case 'M':
          case 'MM':
            month = this._parseSafeInteger(valuePart) - 1;
            break;
          case 'MMMM':
            month = this.monthNames.indexOf(valuePart);
            if (month === -1) throw new Error('Invalid month name');
            break;
          case 'D':
          case 'DD':
            day = this._parseSafeInteger(valuePart);
            break;
          case 'H':
          case 'HH':
            hour = this._parseSafeInteger(valuePart);
            break;
          case 'mm':
            minute = this._parseSafeInteger(valuePart);
            break;
          case 'ss':
            second = this._parseSafeInteger(valuePart);
            break;
          case 'SSS':
            milliseconds = this._parseSafeInteger(valuePart);
            break;
        }
      }
    } else {
      return this.getNanDate();
    }
    return new Date(year, month, day, hour, minute, second, milliseconds);
  }
}

export const dateTimeService = new DateTimeService();